import { useMemo, useState } from 'react';
import { CalendarDays, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { datesInRange, fmtMonth, parseDateOnly, toISODate, todayISO } from '@/lib/dates';

export interface DateRange {
  inicio: string;
  fim: string;
}

interface Props {
  value: DateRange | null;
  onChange: (v: DateRange | null) => void;
  onApply: () => void;
  onCancel: () => void;
  /** Rótulo do botão primário (default: "Aplicar"). */
  applyLabel?: string;
  applyDisabled?: boolean;
  /** Limita o tamanho do intervalo (em dias). Esconde os presets maiores que ele. */
  maxDays?: number;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function fmtBR(iso: string): string {
  const d = parseDateOnly(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function addDays(iso: string, days: number): string {
  const d = parseDateOnly(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/** Semana de segunda a domingo, `offset` semanas à frente. */
function weekRange(offset: number): DateRange {
  const base = parseDateOnly(todayISO());
  const diffSeg = (base.getDay() + 6) % 7;
  const seg = parseDateOnly(addDays(toISODate(base), -diffSeg + offset * 7));
  return { inicio: toISODate(seg), fim: addDays(toISODate(seg), 6) };
}

function monthRange(offset: number): DateRange {
  const base = parseDateOnly(todayISO());
  const first = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const last = new Date(base.getFullYear(), base.getMonth() + offset + 1, 0);
  return { inicio: toISODate(first), fim: toISODate(last) };
}

interface Preset {
  key: string;
  label: string;
  range: () => DateRange;
}

const PRESETS: Preset[] = [
  {
    key: 'next7',
    label: 'Próximos 7 dias',
    range: () => ({ inicio: todayISO(), fim: addDays(todayISO(), 6) }),
  },
  { key: 'thisWeek', label: 'Esta semana', range: () => weekRange(0) },
  { key: 'nextWeek', label: 'Próxima semana', range: () => weekRange(1) },
  {
    key: 'next30',
    label: 'Próximos 30 dias',
    range: () => ({ inicio: todayISO(), fim: addDays(todayISO(), 29) }),
  },
  { key: 'thisMonth', label: 'Este mês', range: () => monthRange(0) },
  { key: 'nextMonth', label: 'Próximo mês', range: () => monthRange(1) },
];

export function DateRangePicker({
  value,
  onChange,
  onApply,
  onCancel,
  applyLabel = 'Aplicar',
  applyDisabled = false,
  maxDays,
}: Props) {
  // Início do range em construção (aguardando o segundo clique).
  const [anchor, setAnchor] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = value ? parseDateOnly(value.inicio) : parseDateOnly(todayISO());
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const presets = useMemo(
    () =>
      maxDays == null
        ? PRESETS
        : PRESETS.filter((p) => {
            const r = p.range();
            return datesInRange(r.inicio, r.fim).length <= maxDays;
          }),
    [maxDays],
  );

  const activePreset = useMemo(() => {
    if (!value) return null;
    return (
      presets.find((p) => {
        const r = p.range();
        return r.inicio === value.inicio && r.fim === value.fim;
      })?.key ?? null
    );
  }, [value, presets]);

  const totalDias = value ? datesInRange(value.inicio, value.fim).length : 0;

  /** Fora do alcance do `maxDays` a partir do primeiro clique. */
  function foraDoLimite(iso: string): boolean {
    if (anchor === null || maxDays == null) return false;
    const [inicio, fim] = iso < anchor ? [iso, anchor] : [anchor, iso];
    return datesInRange(inicio, fim).length > maxDays;
  }

  function pick(iso: string) {
    // Clique fora do limite reancora em vez de gerar um intervalo inválido.
    if (anchor === null || foraDoLimite(iso)) {
      setAnchor(iso);
      onChange({ inicio: iso, fim: iso });
      return;
    }
    const inicio = iso < anchor ? iso : anchor;
    const fim = iso < anchor ? anchor : iso;
    setAnchor(null);
    onChange({ inicio, fim });
  }

  function applyPreset(p: Preset) {
    const r = p.range();
    setAnchor(null);
    onChange(r);
    setViewMonth(() => {
      const d = parseDateOnly(r.inicio);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });
  }

  const months = [viewMonth, new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1)];

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {/* Header — intervalo selecionado + limpar */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <CalendarDays className="h-[18px] w-[18px] shrink-0 text-primary" />
          <span className="truncate font-mono text-[12px] font-semibold uppercase tabular-nums tracking-wider text-foreground">
            {value ? `${fmtBR(value.inicio)} → ${fmtBR(value.fim)}` : 'Selecione o período'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setAnchor(null);
            onChange(null);
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition hover:bg-muted hover:text-primary"
        >
          <X className="h-3.5 w-3.5" />
          Limpar
        </button>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Presets — coluna no desktop, linha rolável no mobile */}
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border bg-surface-2/60 p-2 md:w-44 md:flex-col md:overflow-visible md:border-b-0 md:border-r md:p-3">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-left text-[13px] font-medium transition md:w-full md:py-2.5',
                activePreset === p.key
                  ? 'bg-primary-soft text-primary'
                  : 'text-muted-foreground hover:bg-primary-soft hover:text-primary',
              )}
            >
              {p.label}
            </button>
          ))}
          <div className="hidden md:my-1 md:block md:border-t md:border-border" />
          <span
            className={cn(
              'shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-left text-[13px] font-medium md:w-full md:py-2.5',
              activePreset === null && value
                ? 'bg-primary-soft text-primary'
                : 'text-muted-foreground/70',
            )}
          >
            Período personalizado
          </span>
        </div>

        {/* Calendários — 1 mês no mobile, 2 lado a lado no desktop */}
        <div className="w-full p-4">
          <div className="relative flex flex-col gap-4 md:flex-row md:gap-6">
            <nav className="absolute inset-x-0 top-0 z-10 flex items-center justify-between">
              <button
                type="button"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                }
                aria-label="Mês anterior"
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                }
                aria-label="Próximo mês"
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </nav>

            {months.map((m, i) => (
              <MonthTable
                key={`${m.getFullYear()}-${m.getMonth()}`}
                month={m}
                range={value}
                anchor={anchor}
                onPick={pick}
                isDimmed={foraDoLimite}
                className={i === 1 ? 'hidden md:flex' : undefined}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer — contagem + ações */}
      <div className="flex flex-col gap-2 border-t border-border bg-surface-2/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Período selecionado:{' '}
          <strong className="font-semibold text-foreground">
            {totalDias} {totalDias === 1 ? 'dia' : 'dias'}
          </strong>
          {maxDays != null && <span className="text-muted-foreground/70"> · máx. {maxDays}</span>}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg px-4 py-2 font-mono text-[12px] uppercase tracking-wider text-muted-foreground transition hover:bg-muted sm:flex-none"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={applyDisabled || !value}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2 font-mono text-[12px] font-semibold uppercase tracking-wider text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-50 sm:flex-none"
          >
            <Check className="h-4 w-4" strokeWidth={2.5} />
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

interface MonthTableProps {
  month: Date;
  range: DateRange | null;
  anchor: string | null;
  onPick: (iso: string) => void;
  /** Dia que estouraria o limite do intervalo — mostrado apagado, mas ainda clicável. */
  isDimmed: (iso: string) => boolean;
  className?: string | undefined;
}

function MonthTable({ month, range, anchor, onPick, isDimmed, className }: MonthTableProps) {
  const firstDayIdx = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: Array<number | null> = [
    ...Array.from<unknown, null>({ length: firstDayIdx }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const today = todayISO();

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <div className="mb-2 flex h-9 items-center justify-center px-9 font-mono text-[12px] font-semibold uppercase tracking-wider text-foreground">
        {fmtMonth(month)}
      </div>

      <div className="mb-1 grid grid-cols-7">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="pb-2 text-center text-[11.5px] font-medium text-muted-foreground">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = toISODate(new Date(month.getFullYear(), month.getMonth(), d));
          const isStart = range?.inicio === iso;
          const isEnd = range?.fim === iso;
          const inRange = !!range && iso > range.inicio && iso < range.fim;
          const isEdge = isStart || isEnd;
          const isToday = iso === today;
          const isAnchor = anchor === iso;
          const dimmed = isDimmed(iso);

          return (
            <div
              key={i}
              className={cn(
                'py-0.5 text-center',
                (inRange || (isEdge && range && range.inicio !== range.fim)) && 'bg-primary-soft',
                isStart && range && range.inicio !== range.fim && 'rounded-l-full',
                isEnd && range && range.inicio !== range.fim && 'rounded-r-full',
              )}
            >
              <button
                type="button"
                onClick={() => onPick(iso)}
                className={cn(
                  'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                  isEdge
                    ? 'bg-primary font-semibold text-primary-foreground shadow-red'
                    : inRange
                      ? 'text-primary'
                      : 'text-foreground hover:bg-muted',
                  !isEdge && isToday && 'border border-foreground',
                  isAnchor && 'ring-2 ring-primary/30',
                  dimmed && !isEdge && 'text-muted-foreground/40',
                )}
              >
                {d}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
